import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getSponsorEnforcement, setSponsorEnforcement,
  getStudyEnforcement, setStudyEnforcement,
} from '../../services/reviewWorkflowApi';

/**
 * Approval-enforcement toggle (FECOA-1646).
 *
 * Controls whether the review/approval workflow is *required* before content
 * can be published. Tri-state:
 *   - 'default'  -> null  (inherit: study falls back to sponsor, then env default)
 *   - 'on'       -> true  (always require approval)
 *   - 'off'      -> false (never require approval)
 *
 * Scope is chosen by prop: pass `studyId` for the per-study override (wins over
 * the sponsor setting) or `sponsorId` for the sponsor-wide setting. Internal-admin
 * only — the backend re-checks and rejects non-internal users.
 */
const toEnum = (value) => (value === true ? 'on' : value === false ? 'off' : 'default');
const toValue = (enumv) => (enumv === 'on' ? true : enumv === 'off' ? false : null);

const ApprovalSettingsControl = ({ sponsorId, studyId }) => {
  const isStudy = studyId != null;
  const id = isStudy ? studyId : sponsorId;
  const [state, setState] = useState('default');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    if (id == null) {
      setLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const value = isStudy ? await getStudyEnforcement(id) : await getSponsorEnforcement(id);
        if (active) setState(toEnum(value));
      } catch {
        /* leave at default; backend stays the source of truth */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, isStudy]);

  const handleChange = async (e) => {
    const next = e.target.value;
    const prev = state;
    setState(next);
    setSaving(true);
    try {
      if (isStudy) await setStudyEnforcement(id, toValue(next));
      else await setSponsorEnforcement(id, toValue(next));
      toast.success('Approval enforcement updated.');
    } catch (err) {
      setState(prev);
      toast.error(err?.response?.data?.error || 'Could not update approval enforcement.');
    } finally {
      setSaving(false);
    }
  };

  if (id == null) return null;

  const tip = isStudy
    ? 'Require approval before publishing this study (overrides the sponsor setting)'
    : 'Require approval before publishing assessments for this sponsor';

  return (
    <div
      className="flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white pl-3 pr-2 shadow-xs transition-[border-color,box-shadow] duration-150 focus-within:border-primary-500 focus-within:shadow-focus dark:border-white/10 dark:bg-gray-800"
      title={tip}
    >
      <ShieldCheck className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
      <span className="hidden text-[13px] text-gray-500 dark:text-gray-400 md:inline">Approval</span>
      <select
        value={state}
        onChange={handleChange}
        disabled={loading || saving}
        className="h-full cursor-pointer bg-transparent text-[13px] font-medium text-gray-800 outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:text-gray-100"
      >
        <option value="default">Default {isStudy ? '(inherit sponsor)' : ''}</option>
        <option value="on">Required</option>
        <option value="off">Off</option>
      </select>
    </div>
  );
};

export default ApprovalSettingsControl;
